import PaginatedList from '@theme/paginated-list';

/**
 * A paginated blog-post list with a mobile-only presentation layer.
 *
 * The underlying PaginatedList remains authoritative for fetching and
 * inserting pages. On small screens, this class simply reorganises the
 * rendered cards into:
 * 1) a full-width featured article, and
 * 2) a separate horizontally scrollable "More Recent Guides" rail.
 *
 * Newly appended PaginatedList cards are automatically moved into the rail.
 */
export default class BlogPostsList extends PaginatedList {
  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  /** @type {MutationObserver | null} */
  #layoutObserver = null;

  #layoutScheduled = false;
  #arranging = false;

  connectedCallback() {
    super.connectedCallback();

    this.#mobileQuery = window.matchMedia('(max-width: 749px)');
    this.#mobileQuery.addEventListener('change', this.#handleBreakpointChange);

    const grid = this.refs.grid;
    if (grid instanceof HTMLElement) {
      this.#layoutObserver = new MutationObserver(() => {
        if (!this.#arranging) this.#scheduleLayout();
      });

      // PaginatedList inserts newly fetched article cards directly into
      // the grid. Watching only direct children avoids observing the
      // mobile rail's own internal DOM moves and prevents a re-layout loop.
      this.#layoutObserver.observe(grid, {
        childList: true,
      });
    }

    this.#scheduleLayout();
  }

  disconnectedCallback() {
    this.#layoutObserver?.disconnect();
    this.#layoutObserver = null;

    this.#mobileQuery?.removeEventListener('change', this.#handleBreakpointChange);
    this.#mobileQuery = null;

    super.disconnectedCallback();
  }

  #handleBreakpointChange = () => {
    this.#scheduleLayout();
  };

  #scheduleLayout() {
    if (this.#layoutScheduled) return;

    this.#layoutScheduled = true;
    requestAnimationFrame(() => {
      this.#layoutScheduled = false;
      this.#syncLayout();
    });
  }

  #syncLayout() {
    const grid = this.refs.grid;
    if (!(grid instanceof HTMLElement)) return;

    if (this.#mobileQuery?.matches) {
      this.#arrangeMobile(grid);
    } else {
      this.#restoreDesktop(grid);
    }
  }

  /**
   * @param {HTMLElement} grid
   */
  #arrangeMobile(grid) {
    this.#arranging = true;

    try {
      const allCards = Array.from(grid.querySelectorAll('[ref="cards[]"]')).filter(
        (card) => card instanceof HTMLElement
      );

      let featuredHost = grid.querySelector(':scope > .blog-posts__mobile-featured');
      if (!(featuredHost instanceof HTMLElement)) {
        featuredHost = document.createElement('div');
        featuredHost.className = 'blog-posts__mobile-featured';
      }

      let supporting = grid.querySelector(':scope > .blog-posts__mobile-supporting');
      if (!(supporting instanceof HTMLElement)) {
        supporting = document.createElement('section');
        supporting.className = 'blog-posts__mobile-supporting';

        const header = document.createElement('div');
        header.className = 'blog-posts__mobile-supporting-header';

        const heading = document.createElement('h3');
        heading.className = 'blog-posts__mobile-supporting-heading';
        heading.id = `more-recent-guides-${this.sectionId}`;
        heading.textContent = 'More Recent Guides';

        const cue = document.createElement('span');
        cue.className = 'blog-posts__mobile-supporting-cue';
        cue.id = `more-recent-guides-cue-${this.sectionId}`;
        cue.textContent = 'Swipe or scroll';

        const rail = document.createElement('div');
        rail.className = 'blog-posts__mobile-rail';
        rail.setAttribute('role', 'region');
        rail.setAttribute('aria-labelledby', heading.id);
        rail.setAttribute('aria-describedby', cue.id);
        rail.setAttribute('tabindex', '0');

        header.append(heading, cue);
        supporting.append(header, rail);
      }

      const rail = supporting.querySelector('.blog-posts__mobile-rail');
      if (!(rail instanceof HTMLElement)) return;

      const featured = allCards.find((card) => card.classList.contains('blog-post-item--featured'));

      if (featured && featured.parentElement !== featuredHost) {
        featuredHost.append(featured);
      } else if (!featured) {
        featuredHost.replaceChildren();
      }

      // PaginatedList inserts fetched cards as direct grid children.
      // Move only those newly inserted cards into the rail. Existing rail
      // cards are left untouched, preserving the user's scroll position.
      const newDirectCards = Array.from(grid.children).filter(
        (child) =>
          child instanceof HTMLElement &&
          child.matches('[ref="cards[]"]') &&
          child !== featured
      );

      for (const card of newDirectCards) {
        rail.append(card);
      }

      // Reorder only when a new paginated batch has actually arrived.
      if (newDirectCards.length > 0) {
        const recentCards = Array.from(rail.querySelectorAll('[ref="cards[]"]')).filter(
          (card) => card instanceof HTMLElement
        );

        recentCards
          .sort((a, b) => {
            const pageA = Number(a.dataset.page || 1);
            const pageB = Number(b.dataset.page || 1);
            if (pageA !== pageB) return pageA - pageB;
            return 0;
          })
          .forEach((card) => rail.append(card));
      }

      const recentCards = Array.from(rail.querySelectorAll('[ref="cards[]"]')).filter(
        (card) => card instanceof HTMLElement
      );

      const nextSentinel = this.refs.viewMoreNext;
      if (nextSentinel instanceof HTMLElement) {
        // Avoid re-appending the sentinel on every sync because moving it
        // repeatedly can disturb the user's horizontal scroll position.
        if (nextSentinel.parentElement !== rail || nextSentinel !== rail.lastElementChild) {
          rail.append(nextSentinel);
        }

        const pageNumbers = allCards
          .map((card) => Number(card.dataset.page))
          .filter((page) => Number.isFinite(page));
        const highestLoadedPage = pageNumbers.length ? Math.max(...pageNumbers) : 1;
        const lastPage = Number(grid.getAttribute('last-page') || 1);

        nextSentinel.hidden = highestLoadedPage >= lastPage;
      }

      const lastPage = Number(grid.getAttribute('last-page') || 1);
      const pageNumbers = allCards
        .map((card) => Number(card.dataset.page))
        .filter((page) => Number.isFinite(page));
      const highestLoadedPage = pageNumbers.length ? Math.max(...pageNumbers) : 1;
      supporting.hidden = recentCards.length === 0 && highestLoadedPage >= lastPage;

      if (featuredHost.parentElement !== grid) {
        grid.prepend(featuredHost);
      } else if (grid.firstElementChild !== featuredHost) {
        grid.prepend(featuredHost);
      }

      if (supporting.parentElement !== grid) {
        featuredHost.after(supporting);
      } else if (featuredHost.nextElementSibling !== supporting) {
        featuredHost.after(supporting);
      }

      grid.classList.add('blog-posts-container--mobile-separated');
    } finally {
      this.#arranging = false;
    }
  }

  /**
   * @param {HTMLElement} grid
   */
  #restoreDesktop(grid) {
    const featuredHost = grid.querySelector(':scope > .blog-posts__mobile-featured');
    const supporting = grid.querySelector(':scope > .blog-posts__mobile-supporting');

    if (!(featuredHost instanceof HTMLElement) && !(supporting instanceof HTMLElement)) {
      grid.classList.remove('blog-posts-container--mobile-separated');
      return;
    }

    this.#arranging = true;

    try {
      const cards = Array.from(grid.querySelectorAll('[ref="cards[]"]')).filter(
        (card) => card instanceof HTMLElement
      );

      for (const card of cards) {
        grid.append(card);
      }

      const nextSentinel = this.refs.viewMoreNext;
      if (nextSentinel instanceof HTMLElement) {
        nextSentinel.hidden = false;
        grid.after(nextSentinel);
      }

      featuredHost?.remove();
      supporting?.remove();

      grid.classList.remove('blog-posts-container--mobile-separated');
    } finally {
      this.#arranging = false;
    }
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
